import type { ChannelClient } from '@hibro/platform-core';
import type { IPermissionService, PermissionGrant } from '@hibro/shared';
import {
  PERMISSION_CHANGED_EVENT,
  PERMISSION_RESOLVED_EVENT,
} from '@hibro/shared';
import { Badge, Button, cn } from '@hibro/ui-client';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { useT } from '../i18n/i18n';

export interface PermissionsSectionProps {
  permissionService: IPermissionService;
  /** Channel client, used to refresh on cross-window permission changes. */
  client: ChannelClient;
}

/**
 * Permission management section (架构方案 §5.6). Lists the broker's persisted
 * decisions (`list()`) grouped by module and lets the user revoke a
 * grant/denial (reverting it to undecided so the next access re-prompts).
 * Refreshes when any decision changes — locally or in another window — via the
 * broker's `PERMISSION_CHANGED_EVENT` / `PERMISSION_RESOLVED_EVENT` broadcasts
 * (§4.3). Rendered inside the Settings panel rather than as a standalone modal.
 */
export function PermissionsSection({
  permissionService,
  client,
}: PermissionsSectionProps): ReactNode {
  const t = useT();
  const [grants, setGrants] = useState<PermissionGrant[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    permissionService
      .list()
      .then((next) => {
        setGrants(next);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [permissionService]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const disposeChanged = client.listen(PERMISSION_CHANGED_EVENT, () =>
      load(),
    );
    const disposeResolved = client.listen(PERMISSION_RESOLVED_EVENT, () =>
      load(),
    );
    return () => {
      disposeChanged();
      disposeResolved();
    };
  }, [client, load]);

  const revoke = (grant: PermissionGrant): void => {
    setGrants((prev) =>
      prev.filter(
        (item) =>
          !(
            item.moduleId === grant.moduleId &&
            item.permission === grant.permission
          ),
      ),
    );
    permissionService
      .revoke(grant.moduleId, grant.permission)
      .catch(() => load());
  };

  const byModule = new Map<string, PermissionGrant[]>();
  for (const grant of grants) {
    const list = byModule.get(grant.moduleId) ?? [];
    list.push(grant);
    byModule.set(grant.moduleId, list);
  }

  return (
    <div>
      {error ? (
        <p className="text-[13px] text-destructive">
          {t('workbench.error', { message: error })}
        </p>
      ) : null}
      {grants.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">
          {t('permissions.empty')}
        </p>
      ) : (
        [...byModule.entries()].map(([moduleId, moduleGrants]) => (
          <div key={moduleId} className="mb-4">
            <h4 className="m-0 mb-1.5 text-[13px]">{moduleId}</h4>
            {moduleGrants.map((grant) => (
              <div
                key={grant.permission}
                className="flex items-center gap-2 border-b border-(--app-color-border-subtle) py-1.5"
              >
                <span className="flex-1 text-[13px]">{grant.permission}</span>
                <Badge
                  className={cn(
                    'border-transparent text-(--app-color-on-accent)',
                    grant.state === 'granted' ? 'bg-success' : 'bg-destructive',
                  )}
                >
                  {t(`permissions.state.${grant.state}`)}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => revoke(grant)}
                >
                  {t('permissions.revoke')}
                </Button>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
